import React, { useEffect, useState } from "react";
import { Box, Drawer, Button, IconButton, TextField, MenuItem, Alert } from "@mui/material";
import { useDispatch } from "react-redux";
import { DateTime } from "luxon";
import { IconX, IconCalendarPlus, IconMail } from "@tabler/icons-react";

import { TIMEZONES, defaultTimezone, toGmtSlotPayload, inZone } from "./timezones";

import {
  fetchTimeSlots,
  createTimeSlots,
  sendEmailToCandidate,
} from "../../../services/interviewService";
import { showAppAlert } from "../../../store/reducers/appreducer";
import tokens from "../../theme/tokens";

const { color, font, layout, radius, shadow } = tokens;

const field = {
  "& .MuiOutlinedInput-root": { borderRadius: `${radius.control}px` },
  "& .MuiOutlinedInput-input": { fontSize: 13.5 },
};

const Label = ({ children }) => (
  <Box sx={{ mb: 0.75, font: `500 12px ${font.sans}`, color: color.textSecondary }}>
    {children}
  </Box>
);

const SectionTitle = ({ children }) => (
  <Box
    sx={{
      mb: 1.25,
      font: `600 11px ${font.sans}`,
      color: color.textMuted,
      letterSpacing: ".07em",
    }}
  >
    {children}
  </Box>
);

/**
 * Interview scheduling: open a window of availability, then invite candidates into it.
 *
 * The two steps are ordered on purpose — an invite email contains the slot picker link,
 * so sending invites before any slots exist produces a dead link. The old Interview page
 * enforced this with a disabled button and the label "No time slots available"; here the
 * invite step is visibly gated on the window existing.
 */
const InterviewPanel = ({ open, onClose, jobId, candidates = [], onDone }) => {
  const dispatch = useDispatch();

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [hasSlots, setHasSlots] = useState(false);
  const [checking, setChecking] = useState(true);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !jobId) return;
    setError("");
    setChecking(true);
    fetchTimeSlots(jobId)
      .then((slots) => {
        // What matters is whether any slot is still FREE, not whether slots exist. A window
        // whose every slot is booked is the same as no window: the invite email would send
        // candidates to a picker with nothing to pick.
        const free = Array.isArray(slots) ? slots.filter((s) => !s.booked) : [];
        setHasSlots(free.length > 0);
      })
      .catch(() => setHasSlots(false))
      .finally(() => setChecking(false));
  }, [open, jobId]);

  const handleCreateSlots = async () => {
    if (!start || !end) {
      setError("Pick a start and end for the interview window.");
      return;
    }

    const s = inZone(start, timezone);
    const e = inZone(end, timezone);
    if (!s.isValid || !e.isValid) {
      setError("That doesn't look like a valid date and time.");
      return;
    }
    if (e <= s) {
      setError("The window must end after it starts.");
      return;
    }
    // Compare instants, not wall-clocks — "now" in the chosen zone, not the browser's.
    if (s < DateTime.now()) {
      setError("The interview window can't start in the past.");
      return;
    }

    setError("");
    setCreating(true);
    try {
      // Slots are stored in GMT. The times entered above are wall-clock in the chosen
      // timezone, so they're converted rather than sent raw.
      await createTimeSlots(jobId, toGmtSlotPayload(start, end, timezone));
      setHasSlots(true);
      dispatch(
        showAppAlert({ show: true, alertType: "success", alertMessage: "Interview slots created." })
      );
    } catch (err) {
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: err.message || "Failed to create time slots.",
        })
      );
    } finally {
      setCreating(false);
    }
  };

  const handleSendInvites = async () => {
    setSending(true);
    try {
      const outcomes = await Promise.allSettled(
        candidates.map((c) => sendEmailToCandidate(jobId, c.resumeId))
      );
      const sent = outcomes.filter((o) => o.status === "fulfilled").length;
      const failed = outcomes.length - sent;

      if (!sent) {
        dispatch(
          showAppAlert({ show: true, alertType: "error", alertMessage: "No invites could be sent." })
        );
      } else {
        dispatch(
          showAppAlert({
            show: true,
            alertType: failed ? "warning" : "success",
            alertMessage: failed
              ? `${sent} invite${sent === 1 ? "" : "s"} sent, ${failed} failed.`
              : `${sent} invite${sent === 1 ? "" : "s"} sent.`,
          })
        );
      }
      onDone?.();
      onClose();
    } catch (err) {
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: err.message || "Failed to send invites.",
        })
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: layout.createPanel,
          maxWidth: "100%",
          boxShadow: shadow.panel,
          borderLeft: `1px solid ${color.border}`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", p: 2.75, pb: 2 }}>
        <Box sx={{ flex: 1, font: `600 15px ${font.sans}`, color: color.text }}>
          Schedule interviews
        </Box>
        <IconButton size="small" onClick={onClose}>
          <IconX size={16} stroke={1.75} color={color.textMuted} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 2.75, pb: 2 }}>
        <SectionTitle>1 · INTERVIEW WINDOW</SectionTitle>
        <Box sx={{ font: `400 12px/1.6 ${font.sans}`, color: color.textMuted, mb: 2 }}>
          Candidates pick their own slot inside this window.
          {hasSlots && !checking && " A window already exists — creating another extends it."}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 2 }}>
          <Box>
            <Label>Opens</Label>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              value={start}
              onChange={(e) => {
                setStart(e.target.value);
                setError("");
              }}
              InputLabelProps={{ shrink: true }}
              sx={field}
            />
          </Box>
          <Box>
            <Label>Closes</Label>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              value={end}
              onChange={(e) => {
                setEnd(e.target.value);
                setError("");
              }}
              InputLabelProps={{ shrink: true }}
              sx={field}
            />
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Label>Timezone</Label>
          <TextField
            select
            fullWidth
            size="small"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            sx={field}
          >
            {TIMEZONES.map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ mt: 0.5, font: `400 11px ${font.sans}`, color: color.textMuted }}>
            The times above are read in this timezone. Slots are stored in GMT.
          </Box>
        </Box>

        {error && (
          <Box sx={{ mb: 1.5, font: `400 11.5px ${font.sans}`, color: color.danger }}>{error}</Box>
        )}

        <Button
          variant="outlined"
          onClick={handleCreateSlots}
          disabled={creating}
          startIcon={<IconCalendarPlus size={15} stroke={1.75} />}
        >
          {creating ? "Creating…" : hasSlots ? "Add more slots" : "Create slots"}
        </Button>

        <Box sx={{ mt: 3.5 }}>
          <SectionTitle>2 · INVITE CANDIDATES</SectionTitle>

          {!checking && !hasSlots && (
            <Alert severity="info" sx={{ mb: 2, font: `400 12px ${font.sans}` }}>
              Create an interview window first — the invite email links candidates to the
              slot picker, and without slots that link goes nowhere.
            </Alert>
          )}

          <Box sx={{ font: `400 12px/1.6 ${font.sans}`, color: color.textSecondary, mb: 2 }}>
            {candidates.length
              ? `${candidates.length} candidate${
                  candidates.length === 1 ? "" : "s"
                } in the interview stage will be emailed a link to choose a slot.`
              : "No candidates are in the interview stage yet. Select candidates on the table and choose “Interview” first."}
          </Box>

          <Button
            variant="contained"
            onClick={handleSendInvites}
            disabled={sending || !hasSlots || !candidates.length}
            startIcon={<IconMail size={15} stroke={1.75} />}
          >
            {sending ? "Sending…" : "Send invites"}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: 2.75,
          py: 2,
          borderTop: `1px solid ${color.border}`,
          bgcolor: color.bg,
        }}
      >
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </Box>
    </Drawer>
  );
};

export default InterviewPanel;
